// ============================================
// ThemePicker.jsx - theme menu on the shared ChartMenu shell.
// Opened from the chart right-click menu ("Theme") via a window event
// 'cheddar:open-theme' with detail { x, y } = where the context menu was
// opened; ChartMenu keeps it on screen (phone included) and handles
// Esc / click-outside. Mounted once at app level (App.jsx footer area).
//
// Sections: Themes (pick one) + the active theme's editable colours,
// grouped by THEME_COLOR_GROUPS (themes.js), each swatch labelled.
// Sections fold and remember their state. Edits apply live via useTheme.
// ============================================
import { useState, useEffect } from 'react'
import ChartMenu, { MenuSection, MenuItem, MenuButton } from './menu/ChartMenu'
import ColorPicker from './ColorPicker'
import { listThemes, getTheme, themeFieldValue, THEME_COLOR_GROUPS } from './themes'

const SK = 'solcheddar:themeMenu.'

const CSS = `
.tp-row { display: flex; align-items: center; gap: 6px; padding: 2px 6px; font-size: 11px; color: #bbb; }
.tp-row .tp-label { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tp-reset { background: none; border: none; padding: 0 2px; color: #666; cursor: pointer; font-size: 11px; line-height: 1; }
.tp-reset:hover { color: #ddd; }
.tp-theme-row { position: relative; }
.tp-theme-row .cm-item { width: 100%; }
.tp-del { position: absolute; right: 4px; top: 50%; transform: translateY(-50%); display: none; background: none; border: none; padding: 0 4px; color: #777; cursor: pointer; font-size: 12px; line-height: 1; }
.tp-theme-row:hover .tp-del { display: block; }
.tp-del:hover { color: #ff7b7b; }
.tp-add-input { width: calc(100% - 12px); margin: 2px 6px; box-sizing: border-box; }
.tp-theme-desc { color: #666; font-size: 10px; margin-left: 6px; }
`

export default function ThemePicker({
  themeId,
  setThemeId,
  themeOverrides,           // { [dottedPath]: '#rrggbb' } for the active theme
  setThemeColor,
  resetThemeColor,
  resetThemeColors,
  themesVersion,            // bumps when custom themes are added/removed (re-render)
  addTheme,                 // (name) => newId | null
  deleteTheme,              // (id) => void
}) {
  const [pos, setPos] = useState(null)   // { x, y } when open
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')

  useEffect(() => {
    const onOpen = (e) => {
      const d = e?.detail || {}
      const x = Number.isFinite(d.x) ? d.x : window.innerWidth / 2
      const y = Number.isFinite(d.y) ? d.y : window.innerHeight / 3
      setPos({ x, y })
    }
    window.addEventListener('cheddar:open-theme', onOpen)
    return () => window.removeEventListener('cheddar:open-theme', onOpen)
  }, [])

  if (!pos) return null

  const themes = listThemes()
  const current = themes.find(t => t.id === themeId) || themes[0]
  const baseTheme = getTheme(current.id)
  const overrides = themeOverrides || null
  const overrideCount = overrides ? Object.keys(overrides).length : 0
  const canEdit = typeof setThemeColor === 'function'
  const close = () => { setPos(null); setAdding(false) }
  const canAdd = typeof addTheme === 'function'
  const commitAdd = () => {
    if (!adding) return
    const nm = newName.trim()
    setAdding(false); setNewName('')
    if (!nm) return
    if (themes.some(t => t.name.toLowerCase() === nm.toLowerCase())) return   // de-dupe
    addTheme(nm)
  }

  return (
    <ChartMenu anchor={pos} onClose={close} className="cm-list" title="Theme">
      <style>{CSS}</style>
      <MenuSection title="Themes" collapsible storageKey={SK + 'themes'}>
        {themes.map(t => (
          <div key={t.id} className="tp-theme-row">
            <MenuItem onClick={() => setThemeId(t.id)} title={t.description}
              settingId={t.id === current.id ? 'theme.id' : undefined}>
              {t.id === current.id ? '\u2713' : '\u00A0\u00A0'} {t.name}
            </MenuItem>
            {t.custom && typeof deleteTheme === 'function' && (
              <button type="button" className="tp-del" title={`Delete ${t.name}`}
                onClick={(e) => { e.stopPropagation(); deleteTheme(t.id) }}>{'\u2715'}</button>
            )}
          </div>
        ))}
        {canAdd && (adding ? (
          <input className="cm-text tp-add-input" type="text" autoFocus maxLength={40}
            placeholder="Theme name" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={commitAdd}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') commitAdd()
              else if (e.key === 'Escape') { setAdding(false); setNewName('') }
            }} />
        ) : (
          <MenuItem onClick={() => { setNewName(''); setAdding(true) }} title="Save the current colours as a new theme">
            + Add
          </MenuItem>
        ))}
      </MenuSection>

      {canEdit && THEME_COLOR_GROUPS.map(g => (
        <MenuSection key={g.label} title={`${g.label}`} collapsible defaultOpen={false}
          storageKey={SK + g.label.replace(/\s+/g, '')}>
          {g.fields.map(f => {
            const edited = !!(overrides && overrides[f.path])
            return (
              <div key={f.path} className="tp-row" data-setting-id={`theme.color.${f.path}`}>
                <ColorPicker compact size={14}
                  value={themeFieldValue(baseTheme, overrides, f.path)}
                  onChange={(hex) => setThemeColor(f.path, hex)}
                  title={`${g.label}: ${f.label}`} />
                <span className="tp-label">{f.label}</span>
                {edited && typeof resetThemeColor === 'function' && (
                  <button type="button" className="tp-reset" title={`Reset ${f.label}`}
                    onClick={() => resetThemeColor(f.path)}>{'\u21BA'}</button>
                )}
              </div>
            )
          })}
        </MenuSection>
      ))}

      {canEdit && overrideCount > 0 && typeof resetThemeColors === 'function' && (
        <div style={{ padding: '4px 6px' }}>
          <MenuButton variant="danger" onClick={() => resetThemeColors()}>
            RESET {current.name.toUpperCase()} COLOURS
          </MenuButton>
        </div>
      )}
    </ChartMenu>
  )
}